import React, { useRef, useEffect } from 'react';

const WaterCanvas = ({ particles }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particleSystem = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Initialize advanced particle system
    const initParticles = () => {
      particleSystem.current = Array.from({ length: 80 }, (_, i) => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        size: Math.random() * 3 + 0.5,
        opacity: Math.random() * 0.4 + 0.1,
        life: Math.random() * 1000,
        maxLife: 1000 + Math.random() * 500,
        type: Math.random() > 0.7 ? 'bubble' : 'particle',
        connectionRadius: 120 + Math.random() * 80
      }));
    };

    initParticles();

    // Mouse interaction
    const handleMouseMove = (event) => {
      mouseRef.current = {
        x: event.clientX,
        y: event.clientY
      };
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    // Advanced animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Update and draw particles
      particleSystem.current.forEach((particle, index) => {
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Boundaries with organic bounce
        if (particle.x < 0 || particle.x > canvas.width) {
          particle.vx *= -0.8;
          particle.x = Math.max(0, Math.min(canvas.width, particle.x));
        }
        if (particle.y < 0 || particle.y > canvas.height) {
          particle.vy *= -0.8;
          particle.y = Math.max(0, Math.min(canvas.height, particle.y));
        }

        // Mouse interaction - organic repulsion/attraction
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 150) {
          const force = (150 - distance) / 150;
          particle.vx -= (dx / distance) * force * 0.02;
          particle.vy -= (dy / distance) * force * 0.02;
        }

        // Organic aging
        particle.life++;
        if (particle.life > particle.maxLife) {
          particle.life = 0;
          particle.x = Math.random() * canvas.width;
          particle.y = Math.random() * canvas.height;
          particle.vx = (Math.random() - 0.5) * 0.8;
          particle.vy = (Math.random() - 0.5) * 0.8;
        }

        // Dynamic opacity
        const ageRatio = particle.life / particle.maxLife;
        const pulseOpacity = 0.5 + 0.3 * Math.sin(particle.life * 0.02);
        particle.opacity = (1 - ageRatio * 0.5) * pulseOpacity * 0.3;

        // Draw connections between nearby particles
        particleSystem.current.forEach((other, otherIndex) => {
          if (index !== otherIndex) {
            const dx = other.x - particle.x;
            const dy = other.y - particle.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            if (distance < particle.connectionRadius) {
              const connectionOpacity = (1 - distance / particle.connectionRadius) * 0.1;
              
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = `rgba(20, 184, 166, ${connectionOpacity})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        });

        // Draw particle
        ctx.beginPath();
        
        if (particle.type === 'bubble') {
          // Bubble effect
          const gradient = ctx.createRadialGradient(
            particle.x, particle.y, 0,
            particle.x, particle.y, particle.size
          );
          gradient.addColorStop(0, `rgba(148, 163, 184, ${particle.opacity})`);
          gradient.addColorStop(0.7, `rgba(20, 184, 166, ${particle.opacity * 0.5})`);
          gradient.addColorStop(1, `rgba(59, 130, 246, 0)`);
          
          ctx.fillStyle = gradient;
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
          
          // Bubble highlight
          ctx.beginPath();
          ctx.fillStyle = `rgba(255, 255, 255, ${particle.opacity * 0.3})`;
          ctx.arc(
            particle.x - particle.size * 0.3, 
            particle.y - particle.size * 0.3, 
            particle.size * 0.2, 
            0, Math.PI * 2
          );
          ctx.fill();
          
        } else {
          // Regular particle
          const gradient = ctx.createRadialGradient(
            particle.x, particle.y, 0,
            particle.x, particle.y, particle.size
          );
          gradient.addColorStop(0, `rgba(20, 184, 166, ${particle.opacity})`);
          gradient.addColorStop(1, `rgba(59, 130, 246, 0)`);
          
          ctx.fillStyle = gradient;
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      // Add organic ripples from mouse
      const now = Date.now();
      if (now % 100 < 16) { // Approximately every 100ms
        ctx.beginPath();
        ctx.strokeStyle = `rgba(20, 184, 166, 0.1)`;
        ctx.lineWidth = 1;
        ctx.arc(mouseRef.current.x, mouseRef.current.y, (now % 1000) / 10, 0, Math.PI * 2);
        ctx.stroke();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [particles]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-5 opacity-10"
      style={{ mixBlendMode: 'screen' }}
    />
  );
};

export default WaterCanvas;